import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { animate } from 'animejs';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLock, FiMail } = FiIcons;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const iconRef = useRef(null);

  useEffect(() => {
    if (cardRef.current) {
      animate(cardRef.current, {
        opacity: [0, 1],
        translateY: [40, 0],
        duration: 700,
        ease: 'outExpo',
      });
    }
    if (iconRef.current) {
      animate(iconRef.current, {
        scale: [0, 1],
        rotate: ['-20deg', '0deg'],
        duration: 800,
        delay: 200,
        ease: 'outElastic(1, .6)',
      });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      const result = await login(email, password);
      if (result.success) {
        navigate('/');
      } else {
        setError(result.error || 'Invalid email or password');
        if (cardRef.current) {
          animate(cardRef.current, {
            translateX: [-10, 10, -8, 8, -4, 4, 0],
            duration: 400,
            ease: 'linear',
          });
        }
      }
    } catch {
      setError('An unexpected error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center items-center p-4">
      <div ref={cardRef} className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8" style={{ opacity: 0 }}>
        <div className="text-center mb-8">
          <div ref={iconRef} className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4" style={{ transform: 'scale(0)' }}>
            <SafeIcon icon={FiLock} className="text-2xl text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Login</h1>
          <p className="text-gray-500 mt-2">Manage your weekly expenses securely</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl text-sm border border-red-100">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiMail} className="text-gray-400" />
              </div>
              <input type="email" required
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                placeholder="Enter your email" value={email}
                onChange={(e) => setEmail(e.target.value)} disabled={isSubmitting} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiLock} className="text-gray-400" />
              </div>
              <input type="password" required
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                placeholder="••••••••" value={password}
                onChange={(e) => setPassword(e.target.value)} disabled={isSubmitting} />
            </div>
          </div>
          <button type="submit" disabled={isSubmitting}
            className="w-full flex justify-center py-3 px-4 rounded-xl shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 transition-colors mt-2 disabled:opacity-50">
            {isSubmitting ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
